import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Avatar — a simple round/squared placeholder for user identity.
 *
 * Not building on Radix Avatar because we don't need image-load
 * fallback handling (we only ever show initials in our dashboard).
 * Plain div + sized text is enough.
 *
 * Squircle (8 px radius on 28-32 px element) instead of circle —
 * CF uses rounded squares for the user chip in the header, matches
 * the rest of the chrome's geometry.
 */

interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  // Background tone. Defaults to neutral; pass a tw color shortcut
  // for variety in user lists ("colour code by user name hash").
  tone?: 'neutral' | 'orange';
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size = 28, tone = 'neutral', children, ...props }, ref) => {
    const toneClass =
      tone === 'orange'
        ? 'bg-theia-900/60 text-theia-300'
        : 'bg-neutral-800 text-neutral-200';
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-md font-medium uppercase tracking-tight select-none',
          toneClass,
          className,
        )}
        style={{
          width: size,
          height: size,
          fontSize: Math.max(10, Math.round(size * 0.4)),
        }}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Avatar.displayName = 'Avatar';
